import { PROFESSIONS } from '@continuum/show-core';
import type { CSSProperties } from 'react';
import type { Profession, ProfessionOption } from '@continuum/show-core';
import { Screen } from '../ui/Shell.js';
import { Eyebrow, Lede, PrimaryButton, mono, sans } from '../ui/atoms.js';
import type { ScreenCopy } from '../ui/screenText.js';
import type { Copy, Lang } from '../ui/text.js';

const boardingBg = 'linear-gradient(180deg,#0a0f16,#0b1118)';

/** Check-in — Welcome. The name came with the seat at the door, so this is the first screen. */
export function Welcome({
  t,
  s,
  lang,
  seatLabel,
  crewSize,
  onLang,
  onNext,
}: {
  t: Copy;
  s: ScreenCopy;
  lang: Lang;
  seatLabel: string;
  crewSize: number;
  onLang: (l: Lang) => void;
  onNext: () => void;
}) {
  return (
    <Screen
      style={{ background: boardingBg, color: '#dfe9f2' }}
      footer={
        <>
          <PrimaryButton onClick={onNext}>{t.begin}</PrimaryButton>
          <div style={{ textAlign: 'center', fontFamily: mono, fontSize: 11, letterSpacing: 0.5, color: '#6f808b' }}>
            {s.noInstall}{' '}
            <a href="https://ss-aurora.no/privacy/" target="_blank" rel="noopener" style={{ color: 'inherit' }}>
              {s.privacy}
            </a>
          </div>
        </>
      }
    >
      <Eyebrow>{s.youreIn(seatLabel)}</Eyebrow>

      <div style={{ margin: '30px auto 0', position: 'relative', width: 110, height: 110, flex: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid rgba(79,216,194,.4)' }} />
        <div
          style={{
            position: 'absolute',
            inset: 14,
            borderRadius: '50%',
            border: '1px dashed rgba(79,216,194,.35)',
            animation: 'ccSway 6s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 38,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 30%,#68e0cc,#1a6f61)',
          }}
        />
      </div>

      <h1
        style={{
          margin: '26px 0 8px',
          fontSize: 30,
          lineHeight: 1.05,
          fontWeight: 700,
          letterSpacing: 2,
          textAlign: 'center',
          textWrap: 'balance',
        }}
      >
        {s.welcome}
      </h1>
      <div style={{ fontFamily: mono, fontSize: 13, letterSpacing: 0.5, color: '#9db3c2', textAlign: 'center' }}>{s.tagline}</div>

      <div
        style={{
          margin: '24px 0 0',
          flex: 'none',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          background: 'rgba(255,255,255,.09)',
          border: '1px solid rgba(255,255,255,.09)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {[
          [s.factFrom, s.factFromValue],
          [s.factGuests, String(crewSize)],
        ].map(([label, value]) => (
          <div key={label} style={{ background: '#0d141c', padding: '12px 8px', textAlign: 'center' }}>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.5, color: '#7d8e9c' }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 3 }}>{value}</div>
          </div>
        ))}
      </div>

      <LangSwitch lang={lang} onLang={onLang} style={{ marginTop: 22 }} />
    </Screen>
  );
}

/** EN / NO, as two pills. On the welcome screen, and in CONTROLS. */
export function LangSwitch({
  lang,
  onLang,
  style,
}: {
  lang: Lang;
  onLang: (l: Lang) => void;
  style?: CSSProperties;
}) {
  const pill = (active: boolean): CSSProperties => ({
    minHeight: 44,
    minWidth: 72,
    padding: '0 22px',
    borderRadius: 22,
    border: `1px solid ${active ? '#4fd8c2' : 'rgba(255,255,255,.18)'}`,
    background: active ? 'rgba(79,216,194,.14)' : 'transparent',
    color: '#dfe9f2',
    fontFamily: mono,
    fontSize: 13,
    letterSpacing: 2,
    cursor: 'pointer',
  });
  return (
    <div role="group" aria-label="Language / Språk" style={{ display: 'flex', justifyContent: 'center', gap: 8, ...style }}>
      <button onClick={() => onLang('en')} aria-pressed={lang === 'en'} style={pill(lang === 'en')}>
        EN
      </button>
      <button onClick={() => onLang('no')} aria-pressed={lang === 'no'} style={pill(lang === 'no')}>
        NO
      </button>
    </div>
  );
}

/**
 * Check-in — the job. Capacity comes from the room, so "2 LEFT" is real, and a job closed by
 * the sit-apart rule says so rather than claiming to be full: there is a free place, it is just
 * too close to the guest who already has it.
 */
export function ProfessionPick({
  t,
  s,
  chosen,
  options,
  onChoose,
  onNext,
  pending = false,
  perJob = 2,
}: {
  t: Copy;
  s: ScreenCopy;
  chosen: Profession | null;
  options: readonly ProfessionOption[];
  onChoose: (p: Profession) => void;
  onNext: () => void;
  /** Waiting for the room to confirm the job; the manifest has the last word. */
  pending?: boolean;
  /** Guests per job tonight (`professionCapacity`): one at six guests, two at twelve. */
  perJob?: number;
}) {
  return (
    <Screen
      style={{ background: boardingBg, color: '#dfe9f2' }}
      footer={
        <PrimaryButton big onClick={onNext} disabled={pending || chosen === null}>
          {pending ? s.saving : t.continue}
        </PrimaryButton>
      }
    >
      <Eyebrow>{s.jobEyebrow}</Eyebrow>
      <h1 style={{ margin: '14px 0 6px', fontSize: 26, fontWeight: 700, letterSpacing: 2 }}>{s.jobTitle}</h1>
      <Lede>
        {perJob === 1 ? s.jobLedeOne : s.jobLede} {s.jobEveryEra}
      </Lede>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
        {PROFESSIONS.map((p) => {
          const option = options.find((o) => o.profession === p);
          const left = option?.remaining ?? 0;
          const open = option?.available ?? false;
          const full = !open && chosen !== p;
          // "FULL" only when both places really are taken; otherwise say why: another job has
          // nobody yet (every job gets one guest before any gets two), or a neighbour has this one.
          const first = !open && option?.closed === 'first';
          const tooClose = !open && !first && left > 0;
          const badge = open ? s.jobLeft(left) : first ? s.jobFirst : tooClose ? s.jobTooClose : s.jobFull;
          return (
            <button
              key={p}
              disabled={full}
              onClick={() => onChoose(p)}
              aria-pressed={chosen === p}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
                minHeight: 60,
                padding: '10px 14px',
                borderRadius: 10,
                border: `1px solid ${chosen === p ? '#4fd8c2' : 'rgba(255,255,255,.12)'}`,
                background: chosen === p ? 'rgba(79,216,194,.1)' : 'rgba(255,255,255,.03)',
                color: '#dfe9f2',
                cursor: full ? 'not-allowed' : 'pointer',
                opacity: full ? 0.45 : 1,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 700, letterSpacing: 1.5, fontFamily: sans }}>{p}</span>
                <span style={{ display: 'block', fontFamily: mono, fontSize: 12, color: '#9db3c2', marginTop: 2 }}>
                  {first ? s.jobFirstWhy : tooClose ? s.jobTooCloseWhy : s.jobBlurb[p]}
                </span>
              </span>
              <span
                style={{
                  flex: 'none',
                  fontFamily: mono,
                  fontSize: 11,
                  color: left > 1 && open ? '#4fd8c2' : open ? '#e08a3c' : '#8a9aa6',
                }}
              >
                {badge}
              </span>
            </button>
          );
        })}
      </div>
    </Screen>
  );
}
